import React from 'react';
import TabOfDataTable from '../../../Elements/DataTable/TabOfDataTable';
import SurveyTransferMappingItem from '../../../Domain/SurveyTransferMappingItem';
import SurveyTransferMappingService from '../../../Services/SurveyTransferMappingService';
import SurveyTransferMappingSearchParams from './SurveyTransferMappingSearchParams';
import ErrorMessage from '../../../Elements/ErrorMessage';
import FigmaButtonRow from '../../../Elements/FigmaButtonRow';
import GeneralEditWizzardBase from '../../../Elements/GeneralEditWizzard/GeneralEditWizzardBaseFigma';
import GeneralEditPanelBase from '../../../Elements/GeneralEditPanelBase';
import SurveyTransferMappingWindowDescription from './SurveyTransferMappingWindowDescription';
import SurveyTransferMappingEditPanelDescription from './SurveyTransferMappingEditPanelDescription';
import ShownPanelsEnum from '../../../Enums/ShownPanelsEnum';
import ButtonColorsEnum from '../../../Enums/ButtonColorsEnum';
import ButtonTypeEnum from '../../../Enums/ButtonTypeEnum';
import IconPositionEnum from '../../../Enums/IconPositionEnum';
import CrudTypesEnum from '../../../Enums/CrudTypesEnum';
import OrderDirectionsEnum from '../../../Enums/OrderDirectionsEnum';

// Hardcoded systems for now (see CD-5232).
const SOURCE_SYSTEM_TP_INSTANCE_ID = 11; // PrismIntl
const TARGET_SYSTEM_TP_INSTANCE_ID = 1; // Apollo

class SurveyTransferMappingAdmin extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      shownPanel: ShownPanelsEnum.None,
      // Bumped (new object reference) to make TabOfDataTable reload after a create/search.
      searchItem: new SurveyTransferMappingSearchParams(),
      // The mapping being edited (TargetStatus) in the edit panel, or null.
      editedItem: null,
    };
    this.showPanel = this.showPanel.bind(this);
    this.refresh = this.refresh.bind(this);
    this.startSearch = this.startSearch.bind(this);
    this.startReset = this.startReset.bind(this);
    this.startRefresh = this.startRefresh.bind(this);
    this.startApprove = this.startApprove.bind(this);
    this.startSwitchActiveState = this.startSwitchActiveState.bind(this);
    this.startEdit = this.startEdit.bind(this);
    this.closeEditPanel = this.closeEditPanel.bind(this);
    // Wire the row "Edit" / "Reset" / "Approve" / "Activate-Deactivate" actions.
    SurveyTransferMappingItem.editFunction = this.startEdit;
    SurveyTransferMappingItem.resetFunction = this.startReset;
    SurveyTransferMappingItem.refreshFunction = this.startRefresh;
    SurveyTransferMappingItem.approveFunction = this.startApprove;
    SurveyTransferMappingItem.switchActiveStateFunction = this.startSwitchActiveState;
  }

  // Row "Edit" action: open the edit panel for the mapping (TargetStatus).
  startEdit(item) {
    this.setState({ editedItem: item });
  }

  // GeneralEditPanelBase calls onClose(true) on a successful save.
  closeEditPanel(refresh) {
    this.setState({ editedItem: null });
    if (refresh === true) {
      this.refresh();
    }
  }

  startSearch(searchItem) {
    this.setState({ searchItem: { ...searchItem } });
  }

  // Row "Reset" action: warn that continuing regenerates (deletes + recreates) all of the
  // mapping's rules, then re-run the data import via the microservice.
  startReset(item) {
    ErrorMessage.SetApprove(
      'Resetting will re-import the survey data and DELETE all existing question and answer-option ' +
        'rules of this mapping, then regenerate them. Do you want to continue?',
      () =>
        SurveyTransferMappingService.ResetSurveyTransferMappingData(item)
          .then(() => ErrorMessage.SetNotification('Reset started. The mapping rules are being regenerated.'))
          .catch((error) => ErrorMessage.SetErrorMessage('STMA60', error))
    );
  }

  // Row "Refresh" action: confirm, then reload the source/target survey questions and answer
  // options via the microservice. Non-destructive — existing questions/answer options are updated
  // (new ones inserted) and the mapping rules are left unchanged.
  startRefresh(item) {
    ErrorMessage.SetApprove(
      'Refreshing will reload the source and target survey questions and answer options ' +
        '(updating existing ones and adding new ones). The existing mapping rules will NOT be ' +
        'changed. Do you want to continue?',
      () =>
        SurveyTransferMappingService.RefreshSurveyTransferMappingData(item)
          .then(() =>
            ErrorMessage.SetNotification('Refresh started. The survey questions and answer options are being reloaded.')
          )
          .catch((error) => ErrorMessage.SetErrorMessage('STMA63', error))
    );
  }

  // Row "Approve" action: confirm, then approve the mapping via the microservice.
  startApprove(item) {
    ErrorMessage.SetApprove(
      'Approving will lock this mapping (no further reset or approval). Do you want to continue?',
      () =>
        SurveyTransferMappingService.ApproveSurveyTransferMappingData(item)
          .then(() => {
            ErrorMessage.SetNotification('The mapping has been approved.');
            this.refresh();
          })
          .catch((error) => ErrorMessage.SetErrorMessage('STMA61', error))
    );
  }

  // Row "Activate" / "Deactivate" action: toggle the mapping's IsActive state via the microservice.
  startSwitchActiveState(item) {
    SurveyTransferMappingService.SwitchActiveStateSurveyTransferMappingData(item)
      .then(() => {
        ErrorMessage.SetNotification(
          item.IsActive ? 'The mapping has been deactivated.' : 'The mapping has been activated.'
        );
        this.refresh();
      })
      .catch((error) => ErrorMessage.SetErrorMessage('STMA62', error));
  }

  getNewMapping() {
    return {
      ClientId: '',
      SourceSystemTpInstanceId: SOURCE_SYSTEM_TP_INSTANCE_ID,
      SourceSystemName: 'PrismIntl',
      TargetSystemTpInstanceId: TARGET_SYSTEM_TP_INSTANCE_ID,
      TargetSystemName: 'Apollo',
      SourceTpSurveyId: '',
      TargetTpSurveyId: '',
    };
  }

  showPanel(panelId) {
    panelId = panelId === this.state.shownPanel ? ShownPanelsEnum.None : panelId;
    this.setState({ shownPanel: panelId });
  }

  refresh() {
    // New searchItem reference forces TabOfDataTable to re-fetch the list so the
    // newly created mapping appears.
    this.setState({ shownPanel: ShownPanelsEnum.None, searchItem: { ...this.state.searchItem } });
  }

  renderButtonsAndPanels() {
    return (
      <>
        <FigmaButtonRow
          buttons={[
            {
              label: 'Create New',
              iconName: 'fa-solid fa-plus-circle',
              type: ButtonTypeEnum.NormalIconLeft,
              color: ButtonColorsEnum.Primary,
              iconPosition: IconPositionEnum.Left,
              onClick: () => this.showPanel(ShownPanelsEnum.AddNewItem),
              tooltip: 'Create New Survey Transfer Mapping',
            },
          ]}
        />
        {this.state.shownPanel === ShownPanelsEnum.AddNewItem && (
          <GeneralEditWizzardBase
            crudType={CrudTypesEnum.Create}
            editedItem={this.getNewMapping()}
            clickOnCancel={() => this.setState({ shownPanel: ShownPanelsEnum.None })}
            clickOnSave={this.refresh}
            windowDescription={SurveyTransferMappingWindowDescription}
            errorCode='STMA52'
          />
        )}
      </>
    );
  }

  render() {
    return (
      <div className='contentwrapper'>
        {this.renderButtonsAndPanels()}
        {this.state.editedItem && (
          <GeneralEditPanelBase
            popup={true}
            editedItem={this.state.editedItem}
            panelDescription={SurveyTransferMappingEditPanelDescription}
            onClose={this.closeEditPanel}
            maxWidth={800}
            errorCode='STMA53'
          />
        )}
        <TabOfDataTable
          type={SurveyTransferMappingItem}
          serviceFunction={SurveyTransferMappingService.GetListBySearching}
          convertDBItemsFunction={SurveyTransferMappingService.ConvertDBItems}
          orderBy='ClientName'
          orderDirection={OrderDirectionsEnum.Asc}
          searchItem={this.state.searchItem}
          searchParamsType={SurveyTransferMappingSearchParams}
          showSearchStrip={true}
          singleSearchLine={true}
          singleSearchLinePlaceholder='Search in client, survey names & 3rd-party ids, comment...'
          clickOnStartSearch={this.startSearch}
          shownPanel={this.state.shownPanel}
          errorCode='STMA21'
        />
      </div>
    );
  }
}

export default SurveyTransferMappingAdmin;
